import {ShiftType, type Shift, type VehicleConditionCycleStatus} from "@/src/modules/shifts/types";

import {companyVehiclesService} from "../services";
import {getCurrentShiftCompanyVehicle} from "./shift-company-vehicles";

/**
 * Resolves list/active shift `type` whether the API sends a string (e.g. "patrol")
 * or a labeled object `{ value: "patrol" }`, and tolerates case drift.
 */
export function resolveShiftTypeValue(type: unknown): string | undefined {
  if (type == null) return undefined;
  if (typeof type === "string") return type.toLowerCase();
  if (typeof type === "object" && type !== null && "value" in type) {
    const v = (type as {value: unknown}).value;
    if (typeof v === "string") return v.toLowerCase();
  }
  return undefined;
}

export function isPatrolShiftType(type: unknown): boolean {
  return resolveShiftTypeValue(type) === ShiftType.PATROL;
}

function hasPatrolVehicleConditionPermissions(): boolean {
  if (!companyVehiclesService.getCanCreateConditionCheck()) return false;
  if (!companyVehiclesService.getCanListCompanyVehicles()) return false;
  return true;
}

/**
 * Normalizes cycle status from string (trim/case), labeled `{ value }` objects, or nullish.
 * Unknown strings yield `undefined` so callers can fall back to legacy or custody hints.
 */
function normalizeVehicleConditionCycleStatus(
  raw: unknown,
): VehicleConditionCycleStatus | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (!s) return undefined;
    if (s === "not_applicable" || s === "pending" || s === "started" || s === "ended") {
      return s;
    }
    return undefined;
  }
  if (typeof raw === "object" && raw !== null && "value" in raw) {
    return normalizeVehicleConditionCycleStatus((raw as {value: unknown}).value);
  }
  return undefined;
}

/**
 * Resolves API field whether the client sees snake_case or camelCase, and tolerates
 * value objects / casing differences from the server.
 */
function getCycleStatus(shift: Shift): VehicleConditionCycleStatus | undefined {
  const fromSnake = normalizeVehicleConditionCycleStatus(shift.vehicle_condition_cycle_status);
  if (fromSnake != null) return fromSnake;
  return normalizeVehicleConditionCycleStatus(
    (shift as {vehicleConditionCycleStatus?: unknown}).vehicleConditionCycleStatus,
  );
}

/**
 * After shift start (e.g. home + emergency patrol start): show start-phase vehicle check when API reports `pending`.
 * Does not gate on company-vehicle permissions: the API sets `pending` as the product signal to show the modal
 * (same as legacy `requires_start_vehicle_condition_check` on Home).
 */
export function shouldPromptPatrolStartConditionCheck(shift: Shift | null | undefined): boolean {
  if (!shift) return false;
  const status = getCycleStatus(shift);
  if (status === "pending") return true;
  if (status != null) return false;
  // Legacy flag can stay true when `vehicle_condition_cycle_status` is missing; if the shift
  // already has a company vehicle, do not keep blocking (custody is present — finish flow elsewhere).
  if (getCurrentShiftCompanyVehicle(shift) != null) return false;
  const v = shift.requires_start_vehicle_condition_check;
  return v === true || v === 1;
}

/**
 * When ending a shift: run end-phase check first when the cycle is `started` (start check done, no end check yet).
 */
export function shouldPromptPatrolEndConditionCheck(shift: Shift | null | undefined): boolean {
  if (!shift || !hasPatrolVehicleConditionPermissions()) return false;
  const status = getCycleStatus(shift);
  if (status === "started") {
    return getCurrentShiftCompanyVehicle(shift) != null;
  }
  if (status != null) return false;
  return isPatrolShiftType(shift.type);
}
